import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import tw from "twin.macro";
import styled from "styled-components";
import cogoToast from "cogo-toast";
import Currency from "react-currency-formatter";
import { addTransaction } from "services/backendCalls";

const Overlay = tw.div`fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4`;
const Modal = tw.div`bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-y-auto max-h-screen`;
const Header = tw.div`flex items-center justify-between p-6 border-b border-gray-100`;
const Title = tw.h2`text-xl font-bold text-gray-800`;
const CloseBtn = tw.button`text-gray-400 hover:text-gray-600 font-bold text-2xl leading-none`;
const Body = tw.div`p-6`;

const SummaryBox = tw.div`bg-gray-500 border border-gray-200 rounded-xl p-4 mb-6`;
const SummaryRow = tw.div`flex justify-between items-center py-1 text-sm`;
const SummaryLabel = tw.span`text-gray-500`;
const SummaryValue = tw.span`font-semibold text-gray-800`;
const TotalRow = tw(SummaryRow)`border-t border-gray-200 mt-2 pt-2`;
const TotalLabel = tw(SummaryLabel)`font-bold text-gray-700 text-base`;
const TotalValue = tw(SummaryValue)`text-green-600 text-lg font-bold`;

const FormGroup = tw.div`mb-4`;
const Label = tw.label`block text-sm font-semibold text-gray-700 mb-1`;
const Input = tw.input`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-green-400`;
const ErrorText = tw.p`text-red-500 text-xs mt-1`;

const PaymentToggle = tw.div`grid grid-cols-2 gap-3 mb-6`;
const ToggleBtn = styled.button`
  ${tw`flex flex-col items-center gap-1 p-4 border-2 rounded-xl text-sm font-semibold transition-all duration-200`}
  ${({ selected }) => selected
    ? tw`border-green-500 bg-green-500 text-green-700`
    : tw`border-gray-200 bg-white text-gray-500 hover:border-gray-300`}
`;

const PayBtn = styled.button`
  ${tw`w-full py-4 px-6 text-white font-bold rounded-xl text-base flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200`}
  ${({ pod }) => pod
    ? tw`bg-teal-600 hover:bg-teal-700`
    : tw`bg-green-600 hover:bg-green-700`}
`;

const DEFAULT_VAT = 0.075;

const CoBuyPaymentForm = ({ groupName, amount, user, items: passedItems, onSuccess, onCancel }) => {
  const [paymentMethod, setPaymentMethod] = useState('paystack');
  const [vatRate, setVatRate] = useState(DEFAULT_VAT);
  const [isProcessing, setIsProcessing] = useState(false);
  const [formData, setFormData] = useState({
    email: user?.email || '',
    firstname: user?.displayName?.split(' ')[0] || '',
    lastname: user?.displayName?.split(' ')[1] || '',
    phone: '',
    address: ''
  });
  const [errors, setErrors] = useState({});

  const contribution = Number(amount) || 0;
  const tax = contribution * vatRate;
  const total = contribution + tax;

  useEffect(() => {
    const fetchVat = async () => {
      try {
        const { fetchAppSettings } = await import("DBServices/firestoreDBService");
        const settings = await fetchAppSettings();
        const vatSetting = settings.find(s => s.key === 'vat_percentage');
        if (vatSetting && !isNaN(Number(vatSetting.value))) {
          setVatRate(Number(vatSetting.value) / 100);
        }
      } catch (e) { /* use default */ }
    };
    fetchVat();
  }, []);

  const getPaystackKey = () => {
    try {
      const useLive = process.env.REACT_APP_USE_LIVE_PAYSTACK === 'true';
      return useLive ? process.env.REACT_APP_PAYSTACK_LIVE_KEY : process.env.REACT_APP_PAYSTACK_TEST_KEY;
    } catch { return 'pk_test_48e5da1bf002032842197226fcb852bbcb535eb7'; }
  };

  // Reliably ensure the Paystack inline script (window.PaystackPop) is loaded.
  // Polls for it and injects the script if missing, resolving once ready.
  const ensurePaystackScript = () => new Promise((resolve) => {
    if (window.PaystackPop) return resolve(true);
    const src = 'https://js.paystack.co/v1/inline.js';
    const existing = document.querySelector(`script[src="${src}"]`);
    const start = Date.now();
    const poll = setInterval(() => {
      if (window.PaystackPop) {
        clearInterval(poll);
        resolve(true);
      } else if (Date.now() - start > 8000) {
        clearInterval(poll);
        resolve(false);
      }
    }, 100);
    if (!existing) {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      document.body.appendChild(script);
    }
  });

  const payWithPaystack = async () => {
    const ready = await ensurePaystackScript();
    if (!ready) {
      cogoToast.error('Could not initialize payment. Try again.');
      setIsProcessing(false);
      return;
    }
    window.PaystackPop.setup({
      key: getPaystackKey(),
      email: formData.email,
      amount: Math.round(total * 100),
      ref: `COBUY_${Date.now()}`,
      currency: 'NGN',
      callback: (reference) => handlePaystackSuccess(reference),
      onClose: () => {
        cogoToast.info('Payment cancelled.');
        setIsProcessing(false);
      }
    }).openIframe();
  };

  const validate = () => {
    const e = {};
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) e.email = 'Valid email required';
    if (!formData.firstname || formData.firstname.length < 2) e.firstname = 'First name required';
    if (!formData.lastname || formData.lastname.length < 2) e.lastname = 'Last name required';
    if (!formData.phone || formData.phone.length < 10) e.phone = 'Valid phone required';
    if (paymentMethod === 'pod' && (!formData.address || formData.address.length < 5)) e.address = 'Delivery address required for Pay on Delivery';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(p => ({ ...p, [name]: value }));
    if (errors[name]) setErrors(p => ({ ...p, [name]: '' }));
  };

  // Co-buy contributions may be passed structured items (e.g. the group's
  // marketplace products). If none are supplied, fall back to a single
  // descriptive line item so the transaction history shows what was bought.
  const buildCoBuyItems = () => {
    if (Array.isArray(passedItems) && passedItems.length > 0) return passedItems;
    return [{
      title: groupName || 'Co-Buy Contribution',
      quantity: 1,
      price: contribution,
      total: contribution,
      imageSrc: '',
      basket: []
    }];
  };

  const saveTransaction = async (reference, status, extra = {}) => {
    const data = {
      reference,
      status,
      type: 'co_buy_contribution',
      groupName: groupName || 'Co-Buy Group',
      items: buildCoBuyItems(),
      userId: user?.uid || 'guest',
      userEmail: formData.email,
      userName: `${formData.firstname} ${formData.lastname}`,
      userPhone: formData.phone,
      deliveryAddress: formData.address || '',
      contribution,
      tax,
      total,
      vatRate: (vatRate * 100).toFixed(1),
      createdAt: new Date().toISOString(),
      timestamp: new Date().toISOString(),
      ...extra
    };
    await addTransaction(data);
  };

  const handlePaystackSuccess = async (reference) => {
    try {
      await saveTransaction(
        reference.reference || `COBUY_${Date.now()}`,
        reference.status || 'success',
        { paymentMethod: 'paystack', message: reference.message || 'Payment successful' }
      );
      cogoToast.success('Payment successful! You have joined the co-buy pool.');
    } catch (e) {
      console.error('Error saving co-buy transaction:', e);
      cogoToast.success('Payment successful!');
    }
    onSuccess && onSuccess({ reference, amount: contribution, paidVia: 'paystack' });
  };

  const handlePOD = async () => {
    setIsProcessing(true);
    try {
      const reference = `COBUY_POD_${Date.now()}`;
      await saveTransaction(reference, 'pay_on_delivery_pending', {
        paymentMethod: 'pay_on_delivery',
        message: 'Cash payment to be collected on delivery'
      });
      cogoToast.success('Co-buy interest registered! Pay on delivery when your order arrives.');
      onSuccess && onSuccess({ reference, amount: contribution, paidVia: 'pod' });
    } catch (e) {
      cogoToast.error('Failed to place order. Please try again.');
      console.error('POD error:', e);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSubmit = () => {
    if (!validate()) {
      cogoToast.error('Please fill in all required fields.');
      return;
    }
    if (paymentMethod === 'pod') {
      handlePOD();
    } else {
      setIsProcessing(true);
      payWithPaystack();
    }
  };

  return ReactDOM.createPortal(
    <Overlay onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <Modal>
        <Header>
          <Title>Complete Your Contribution</Title>
          <CloseBtn onClick={onCancel}>×</CloseBtn>
        </Header>
        <Body>
          <SummaryBox>
            <p style={{ fontWeight: 700, fontSize: '0.85rem', color: '#374151', marginBottom: 8 }}>
              🛒 Co-Buy Group: <span style={{ color: '#059669' }}>{groupName}</span>
            </p>
            <SummaryRow>
              <SummaryLabel>Contribution Amount</SummaryLabel>
              <SummaryValue><Currency quantity={contribution} currency="NGN" /></SummaryValue>
            </SummaryRow>
            <SummaryRow>
              <SummaryLabel>VAT ({(vatRate * 100).toFixed(1)}%)</SummaryLabel>
              <SummaryValue><Currency quantity={tax} currency="NGN" /></SummaryValue>
            </SummaryRow>
            <TotalRow>
              <TotalLabel>Total Due</TotalLabel>
              <TotalValue><Currency quantity={total} currency="NGN" /></TotalValue>
            </TotalRow>
          </SummaryBox>

          <PaymentToggle>
            <ToggleBtn type="button" selected={paymentMethod === 'paystack'} onClick={() => setPaymentMethod('paystack')}>
              <span style={{ fontSize: '1.4rem' }}>💳</span>
              <span>Pay Online</span>
              <span style={{ fontSize: '0.7rem', fontWeight: 400 }}>Card / Transfer</span>
            </ToggleBtn>
            <ToggleBtn type="button" selected={paymentMethod === 'pod'} onClick={() => setPaymentMethod('pod')}>
              <span style={{ fontSize: '1.4rem' }}>💵</span>
              <span>Pay on Delivery</span>
              <span style={{ fontSize: '0.7rem', fontWeight: 400 }}>Cash when delivered</span>
            </ToggleBtn>
          </PaymentToggle>

          <FormGroup>
            <Label>Email *</Label>
            <Input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="you@example.com" disabled={!!user?.email} />
            {errors.email && <ErrorText>{errors.email}</ErrorText>}
          </FormGroup>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FormGroup>
              <Label>First Name *</Label>
              <Input type="text" name="firstname" value={formData.firstname} onChange={handleChange} placeholder="John" />
              {errors.firstname && <ErrorText>{errors.firstname}</ErrorText>}
            </FormGroup>
            <FormGroup>
              <Label>Last Name *</Label>
              <Input type="text" name="lastname" value={formData.lastname} onChange={handleChange} placeholder="Doe" />
              {errors.lastname && <ErrorText>{errors.lastname}</ErrorText>}
            </FormGroup>
          </div>
          <FormGroup>
            <Label>Phone *</Label>
            <Input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="08012345678" />
            {errors.phone && <ErrorText>{errors.phone}</ErrorText>}
          </FormGroup>
          {paymentMethod === 'pod' && (
            <FormGroup>
              <Label>Delivery Address *</Label>
              <Input type="text" name="address" value={formData.address} onChange={handleChange} placeholder="15 Awolowo Road, Lagos" />
              {errors.address && <ErrorText>{errors.address}</ErrorText>}
            </FormGroup>
          )}

          <PayBtn pod={paymentMethod === 'pod' ? 1 : 0} onClick={handleSubmit} disabled={isProcessing}>
            {isProcessing
              ? 'Processing...'
              : paymentMethod === 'pod'
                ? '📦 Confirm & Join (Pay on Delivery)'
                : <>Pay <Currency quantity={total} currency="NGN" /> & Join Pool</>
            }
          </PayBtn>
        </Body>
      </Modal>
    </Overlay>,
    document.body
  );
};

export default CoBuyPaymentForm;
